import { useState, useEffect } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { BrushButton } from "@/components/ui/brush-button";
import { Link } from "react-router-dom";
import { MessageCircle, Video, Headphones, BookOpen, Briefcase } from "lucide-react";
import ScrollReveal from "@/components/animations/ScrollReveal";
import chatCtaBg from "@/assets/chat-cta-bg.webp";
const ChatCTASection = () => {
  const {
    t
  } = useLanguage();
  const [contentOpacity, setContentOpacity] = useState(0);
  const [bgVisible, setBgVisible] = useState(false);
  useEffect(() => {
    let rafId: number;
    const handleScroll = () => {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        rafId = 0;
        const section = document.getElementById("chat-cta-section");
        const content = document.getElementById("chat-cta-content");
        if (!section || !content) return;
        const sectionRect = section.getBoundingClientRect();
        const contentRect = content.getBoundingClientRect();
        const contentTop = contentRect.top;
        const vh = window.innerHeight;

        // Show background only when section is in viewport, hide before reaching footer
        setBgVisible(sectionRect.top < vh && sectionRect.bottom > vh * 0.3);

        // Fade-in only - no fade-out, content stays visible
        const fadeInStart = vh * 0.8;
        const fadeInEnd = vh * 0.3;
        let opacity = 1;
        if (contentTop > fadeInStart) {
          opacity = 0;
        } else if (contentTop > fadeInEnd) {
          opacity = (fadeInStart - contentTop) / (fadeInStart - fadeInEnd);
        } else {
          // Stay fully visible once faded in
          opacity = 1;
        }
        setContentOpacity(Math.max(0, Math.min(1, opacity)));
      });
    };
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    handleScroll();
    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, []);
  const tags = [{
    icon: Video,
    label: "Videos"
  }, {
    icon: Headphones,
    label: "Podcast"
  }, {
    icon: BookOpen,
    label: "Guides"
  }, {
    icon: Briefcase,
    label: "Jobs"
  }];
  return <section id="chat-cta-section" className="relative min-h-[90vh] md:min-h-[120vh]">
      {/* Fixed background image - full visibility, no overlay */}
      <div className="fixed inset-0 z-0 h-screen bg-cover bg-[70%_center] md:bg-center bg-no-repeat transition-opacity duration-300" style={{
      backgroundImage: `url(${chatCtaBg})`,
      opacity: bgVisible ? 1 : 0,
      pointerEvents: bgVisible ? "auto" : "none"
    }} />

      {/* Initial spacer - pushes content to center of viewport */}
      <div className="h-[40vh] md:h-[70vh]" aria-hidden="true" />

      {/* Content with fade-in effect - stays visible */}
      <div id="chat-cta-content" className="relative z-10 py-section-mobile md:py-section-desktop" style={{
      opacity: contentOpacity,
      transition: "opacity 0.15s ease-out"
    }}>
        <div className="container mx-auto px-md lg:px-lg">
          <div className="max-w-xl bg-forest-900/85 backdrop-blur-sm rounded-2xl p-lg">
            <div className="text-left">
              <ScrollReveal delay={80}>
                <h2 className="text-h2 md:text-[40px] font-bold text-sand-50 mb-md">{t("chatCta.title")}</h2>
              </ScrollReveal>
              <ScrollReveal delay={160}>
                <p className="text-body text-sand-100/90 mb-lg max-prose">{t("chatCta.subtitle")}</p>
              </ScrollReveal>

              {/* Resource tags */}
              <ScrollReveal delay={240}>
                <div className="flex flex-wrap gap-sm mb-xl justify-start">
                  {tags.map((tag, index) => <span key={index} className="inline-flex items-center gap-xs px-4 py-2 bg-sand-50/20 text-sand-50 rounded-pill text-small font-medium hover:bg-sand-50/30 transition-colors duration-fast cursor-pointer">
                      <tag.icon className="h-4 w-4" />
                      {tag.label}
                    </span>)}
                </div>
              </ScrollReveal>

              <ScrollReveal delay={320}>
                <Link to="/chat">
                  <BrushButton className="w-full sm:w-auto">
                    <span className="inline-flex items-center my-0 px-0 py-0 text-center text-lg">
                      <MessageCircle className="mr-2 h-5 w-5" />
                      {t("chatCta.button")}
                    </span>
                  </BrushButton>
                </Link>
              </ScrollReveal>
            </div>
          </div>
        </div>
      </div>

      {/* Reduced spacer - footer appears soon after content */}
      <div className="h-[20vh] md:h-[30vh]" aria-hidden="true" />
    </section>;
};
export default ChatCTASection;